import type { FunctionComponent } from "react";
import clsx from "clsx";
import usePagination from "~/utils/usePagination";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";

interface PaginationComponentProps {
  totalPosts: number;
  currentPage: number;
  postsPerPage: number;
  urlBase: string;
}

export const Pagination: FunctionComponent<PaginationComponentProps> = ({
  totalPosts,
  currentPage,
  postsPerPage,
  urlBase,
}) => {
  const { paginationRange } = usePagination({
    totalPosts,
    currentPage,
    postsPerPage,
    siblingCount: 1, // 現在のページの前後に表示するページ数
  });

  if (!paginationRange) return null;
  return (
    <div>
      <ul className={clsx("flex", "justify-center", "gap-12")}>
        {paginationRange.map((pageNumber, index) => (
          <PaginationItem
            key={index}
            pageNumber={pageNumber}
            currentPage={currentPage}
            urlBase={urlBase}
          />
        ))}
      </ul>
    </div>
  );
};

export const paginationStyle = cva(
  [
    "px-4",
    "py-2",
    "inline-flex",
    "items-center",
    "justify-center",
    "rounded-md",
    "text-sm",
    "font-medium",
    "transition-colors",
  ],
  {
    variants: {
      variant: {
        number: [
          "bg-white",
          "text-gray-700",
          "hover:bg-gray-50",
          "border",
          "border-gray-300",
        ],
        ellipsis: ["bg-white", "text-gray-400", "cursor-default"],
        current: ["bg-black", "text-white", "border", "border-transparent"],
      },
      size: {
        default: ["h-40", "w-40"],
        sm: ["h-20", "w-20", "text-xs"],
        lg: ["h-65", "w-65", "text-13"],
      },
    },
    defaultVariants: {
      variant: "number",
      size: "default",
    },
  },
);

export type PaginationStyleProps = VariantProps<typeof paginationStyle>;

export interface PaginationItemProps extends PaginationStyleProps {
  pageNumber: number | string;
  currentPage: number;
  urlBase: string;
}

export const PaginationItem: React.FC<PaginationItemProps> = ({
  pageNumber,
  currentPage,
  urlBase,
  size,
}) => {
  const isCurrentPage = currentPage === pageNumber;
  const isEllipsis = pageNumber === "...";

  const className = paginationStyle({
    variant: isCurrentPage ? "current" : isEllipsis ? "ellipsis" : "number",
    size,
  });

  if (isCurrentPage) {
    return (
      <li>
        <span className={className} aria-current="page">
          {pageNumber}
        </span>
      </li>
    );
  }

  if (isEllipsis) {
    return (
      <li>
        <span className={className} aria-hidden="true">
          &hellip;
        </span>
      </li>
    );
  }

  return (
    <li>
      <a href={`${urlBase}?page=${pageNumber}`} className={className}>
        {pageNumber}
      </a>
    </li>
  );
};
